import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  getAuth, 
  signInWithPhoneNumber,
  RecaptchaVerifier
} from "firebase/auth";
import { 
  Button, 
  TextField, 
  Typography, 
  Box, 
  InputAdornment,
  CircularProgress,
  Grid,
  Snackbar,
  Alert
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { auth } from '../firebase-config';

const PhoneLogin = ({ onReset }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
  const [recaptchaError, setRecaptchaError] = useState(false);
  const inputRefs = useRef([]);
  const phoneInputRef = useRef(null);
  const recaptchaVerifier = useRef(null);
  const recaptchaContainerId = 'recaptcha-container';
  const isMounted = useRef(true);

  const initializeRecaptcha = useCallback(() => {
    if (recaptchaVerifier.current) {
      recaptchaVerifier.current.clear();
    }
    recaptchaVerifier.current = new RecaptchaVerifier(auth, recaptchaContainerId, {
      'size': 'invisible',
      'callback': () => {
        if (isMounted.current) {
          setRecaptchaError(false);
        }
      },
      'expired-callback': () => {
        if (isMounted.current) {
          setSnackbar({ open: true, message: 'reCAPTCHA expired. Please try again.', severity: 'warning' });
          setRecaptchaError(true);
          onReset();
        }
      }
    });
  }, [onReset]);

  useEffect(() => {
    initializeRecaptcha();
    if (phoneInputRef.current) {
      phoneInputRef.current.focus();
    }

    return () => {
      isMounted.current = false;
      if (recaptchaVerifier.current) {
        recaptchaVerifier.current.clear();
      }
    };
  }, [initializeRecaptcha]);

  useEffect(() => {
    if (user && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [user]);

  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formattedPhoneNumber = formatPhoneNumber(e.target.value);
    setPhoneNumber(formattedPhoneNumber);
  };

  const handleVerificationCodeChange = (index, value) => {
    if (isNaN(value)) return; // Only allow numeric input
    const newVerificationCode = [...verificationCode];
    newVerificationCode[index] = value;
    setVerificationCode(newVerificationCode);

    // Auto-focus next input
    if (value !== '' && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && verificationCode[index] === '' && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const sendVerificationCode = async () => {
    setLoading(true);
    setError(null);
    setRecaptchaError(false);
    const formattedPhoneNumber = '+1' + phoneNumber.replace(/\D/g, '');
    try {
      const appVerifier = recaptchaVerifier.current;
      if (!appVerifier) {
        throw new Error('reCAPTCHA has not been properly initialized.');
      }
      
      // Set a timeout for the reCAPTCHA verification
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('reCAPTCHA verification timed out')), 30000)
      );
      
      const confirmationPromise = signInWithPhoneNumber(auth, formattedPhoneNumber, appVerifier);
      
      // Race the confirmation promise against the timeout
      const confirmation = await Promise.race([confirmationPromise, timeoutPromise]);
      
      if (isMounted.current) {
        setUser(confirmation);
        setSnackbar({ open: true, message: 'Verification code sent successfully!', severity: 'success' });
      }
    } catch (e) {
      console.error('Firebase Auth Error:', e);
      let errorMessage = 'An error occurred while sending the verification code.';
      if (e.code === 'auth/invalid-phone-number') {
        errorMessage = 'The phone number is invalid. Please check and try again.';
      } else if (e.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please try again later.';
      } else if (e.message === 'reCAPTCHA verification timed out') {
        errorMessage = 'reCAPTCHA verification timed out. Please try again.';
        setRecaptchaError(true);
      }
      if (isMounted.current) {
        setError(errorMessage);
        setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        if (e.message === 'reCAPTCHA verification timed out') {
          onReset();
        }
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const verifyCode = async () => {
    setLoading(true);
    setError(null);
    const code = verificationCode.join('');
    try {
      await user.confirm(code);
      if (isMounted.current) {
        setSnackbar({ open: true, message: 'Verification successful!', severity: 'success' });
      }
      // Handle successful verification here
    } catch (e) {
      console.error('Firebase Auth Error:', e);
      let errorMessage = 'Incorrect verification code. Please try again.';
      if (e.code === 'auth/invalid-verification-code') {
        errorMessage = 'The verification code is invalid. Please check and try again.';
      } else if (e.code === 'auth/code-expired') {
        errorMessage = 'The verification code has expired. Please request a new one.';
      }
      if (isMounted.current) {
        setError(errorMessage);
        setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        setVerificationCode(['', '', '', '', '', '']);
        inputRefs.current[0].focus();
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleResetRecaptcha = () => {
    initializeRecaptcha();
    setRecaptchaError(false);
    onReset();
  };

  return (
    <Box sx={{ maxWidth: 400, margin: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <img 
        src="/logo.png" 
        alt="Beast Mode Logo" 
        style={{ width: '90%', marginBottom: 16 }} 
      />
      {user === null ? (
        <>
          <TextField
            fullWidth
            label="Phone Number"
            variant="outlined"
            value={phoneNumber}
            onChange={handlePhoneChange}
            margin="normal"
            placeholder="(123) 456-7890"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon />
                </InputAdornment>
              ),
            }}
            inputRef={phoneInputRef}
            autoFocus
            inputProps={{
              'aria-label': 'Phone number',
            }}
          />
          <div id={recaptchaContainerId} aria-hidden="false"></div>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={sendVerificationCode}
            fullWidth
            sx={{ mt: 2 }}
            disabled={loading || phoneNumber.length < 14}
          >
            {loading ? <CircularProgress size={24} /> : 'Send Verification Code'}
          </Button>
          {recaptchaError && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleResetRecaptcha}
              fullWidth
              sx={{ mt: 2 }}
            >
              Reset reCAPTCHA
            </Button>
          )}
        </>
      ) : (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Enter the 6-digit verification code sent to your phone
          </Typography>
          <Grid container spacing={1} justifyContent="center" sx={{ mb: 2 }}>
            {verificationCode.map((digit, index) => (
              <Grid item key={index} xs={2}>
                <TextField
                  inputRef={(el) => (inputRefs.current[index] = el)}
                  variant="outlined"
                  value={digit}
                  onChange={(e) => handleVerificationCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  inputProps={{
                    maxLength: 1,
                    style: { textAlign: 'center', width: '100%', height: '40px', fontSize: '1.5rem' },
                    'aria-label': `Verification code digit ${index + 1}`,
                  }}
                />
              </Grid>
            ))}
          </Grid>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={verifyCode}
            fullWidth
            disabled={loading || verificationCode.join('').length !== 6}
          >
            {loading ? <CircularProgress size={24} /> : 'Verify Code'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onReset}
            fullWidth
            sx={{ mt: 2 }}
            startIcon={<ArrowBackIcon />}
          >
            Try Different Number
          </Button>
        </>
      )}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const PhoneLoginWrapper = () => {
  const [key, setKey] = useState(0);

  const handleReset = () => {
    setKey(prevKey => prevKey + 1);
  };

  return <PhoneLogin key={key} onReset={handleReset} />;
};

export default PhoneLoginWrapper;